import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { MaterialMapper } from "@/components/calculators/MaterialMapper";
import { linesFromTerrassebord } from "@/components/calculators/lineMappers";
import { terrassebordCalc } from "@/domain/calculators/carpenter";
import type { CalcQuantity } from "@/lib/calc-to-lines";

export const Route = createFileRoute("/_authenticated/calculations/carpenter/terrassebord")({
  component: TerrassebordPage,
});

function TerrassebordPage() {
  const [area, setArea] = useState<number | "">("");
  const [bredde, setBredde] = useState<number | "">(120);
  const [spalte, setSpalte] = useState<number | "">(5);
  const [lengde, setLengde] = useState<number | "">(4.2);
  const [spill, setSpill] = useState<number | "">(10);

  const inputs = useMemo(
    () => ({
      areal_m2: Number(area),
      bord_bredde_mm: Number(bredde),
      spalte_mm: spalte === "" ? undefined : Number(spalte),
      bord_lengde_m: lengde === "" ? undefined : Number(lengde),
    }),
    [area, bredde, spalte, lengde],
  );

  const r = useMemo(() => {
    if (area === "" || bredde === "") return null;
    return terrassebordCalc({ ...inputs, spill_pct: spill === "" ? undefined : Number(spill) });
  }, [area, bredde, inputs, spill]);

  // Grunnmengder uten spill – spill styres per rad i materiell-steget.
  const base = useMemo(() => {
    if (area === "" || bredde === "") return null;
    return terrassebordCalc({ ...inputs, spill_pct: 0 });
  }, [area, bredde, inputs]);

  const quantities: CalcQuantity[] = useMemo(() => {
    if (!base) return [];
    return [
      {
        key: "bord",
        label: "Terrassebord",
        quantity: Math.round(base.lopemeter * 10) / 10,
        unit: "lm",
        defaultSpillPct: 8,
        suggestedGroupCode: "0506",
        searchHint: "terrassebord",
      },
      {
        key: "skruer",
        label: "Terrasseskruer",
        quantity: Math.ceil(base.skruer),
        unit: "stk",
        defaultSpillPct: 5,
        searchHint: "terrasseskrue",
      },
    ];
  }, [base]);


  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Terrassebord" subtitle="Beregn løpemeter terrassebord og skruer." />
      <CalcShell
        title="Inndata"
        result={r ? (
          <>
            <ResultCard
            title="Materialbehov"
            rows={[
              { label: "Løpemeter bord", value: `${fmt(r.lopemeter, 1)} m`, strong: true },
              { label: "Antall bord",    value: `${fmt(r.antall_bord, 0)} stk` },
              { label: "Skruer",         value: `${fmt(r.skruer, 0)} stk` },
            ]}
            note="Antar ca 40 skruer pr m². Juster svinn ved diagonal/mønster­legging."
          />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Terrassebord`} lines={linesFromTerrassebord(r, Number(bredde))} defaultLaborHours={Math.max(0, Math.round((Number(area) * 0.4) * 10) / 10)} />
            </div>
            <MaterialMapper
              quantities={quantities}
              calc={{ key: "terrassebord", version: 1, inputs }}
              sourceLabel="Terrassebord-kalkulatoren"
              moduleKey="carpenter"
            />
          </>

        ) : <div className="card p-5 text-sm text-muted-foreground">Fyll inn areal og bordbredde.</div>}
      >
        <Field label="Terrasse­areal" unit="m²"><NumberInput value={area} onChange={setArea} /></Field>
        <Field label="Bord­bredde" unit="mm"><NumberInput value={bredde} onChange={setBredde} /></Field>
        <Field label="Spalte mellom bord" unit="mm"><NumberInput value={spalte} onChange={setSpalte} /></Field>
        <Field label="Bord­lengde" unit="m"><NumberInput value={lengde} onChange={setLengde} /></Field>
        <Field label="Svinn" unit="%"><NumberInput value={spill} onChange={setSpill} /></Field>
      </CalcShell>
    </>
  );
}
